import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { CollisionFacet, isCompoundShape, VisibilityFacet, worldToLocalTransform } from "flavr-common";
import { modelPool } from "@modules/model";
/**
 * Compound collision shapes are not baked at parse time (server or client blueprint parser) - a model's
 * `CollisionFacet` just marks it as compound with an empty element list. This manager derives the actual
 * `CompoundElement[]` live from whichever descendants have their own `CollisionFacet`, and keeps it up to date as
 * children (or their CollisionFacets) are attached/removed.
 *
 * Descendants are flattened regardless of depth (matching the previous server-side node-walk behavior): if a
 * descendant itself has a compound CollisionFacet, it isn't embedded as a nested element (the wire format doesn't
 * support nested compounds) - instead we keep recursing through it so its own eventual leaf shapes still end up in
 * this compound's element list.
 */
export class CompoundColliderManager {
    constructor(onRebuild) {
        this.onRebuild = onRebuild;
        this.registrations = new Map();
        this.pendingRebuildUIDs = new Set();
    }
    register(uid) {
        if (this.registrations.has(uid)) {
            this.scheduleRebuild(uid);
            return;
        }
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const unsubscribes = [
            modelPool.setOnModelFacetChangedHandler((changedModel, facet) => {
                var _a, _b;
                if (changedModel.getUID() === uid)
                    return;
                // A descendant's own CollisionFacet always matters. A descendant's VisibilityFacet only matters when this
                // compound is configured to ignore invisible children - otherwise visibility swaps (e.g. a training object
                // toggling between mutually-exclusive state models) would trigger rebuilds for no reason.
                const isRelevantFacetChange = CollisionFacet.is(facet)
                    || (VisibilityFacet.is(facet) && ((_b = (_a = model.getFacet(CollisionFacet)) === null || _a === void 0 ? void 0 : _a.ignoreInvisibleChildren) !== null && _b !== void 0 ? _b : false));
                if (!isRelevantFacetChange)
                    return;
                if (!this.isDescendantOfOrSelf(changedModel, uid))
                    return;
                this.scheduleRebuild(uid);
            }),
            modelPool.setOnModelFacetRemovedHandler((changedModel, facet) => {
                if (changedModel.getUID() === uid)
                    return;
                if (!CollisionFacet.is(facet))
                    return;
                if (!this.isDescendantOfOrSelf(changedModel, uid))
                    return;
                this.scheduleRebuild(uid);
            }),
            modelPool.setOnModelChildAddedHandler((parent) => {
                if (!this.isDescendantOfOrSelf(parent, uid))
                    return;
                this.scheduleRebuild(uid);
            }),
            modelPool.setOnModelChildRemovedHandler((parent) => {
                if (!this.isDescendantOfOrSelf(parent, uid))
                    return;
                this.scheduleRebuild(uid);
            })
        ];
        this.registrations.set(uid, unsubscribes);
        this.scheduleRebuild(uid);
    }
    unregister(uid) {
        const unsubscribes = this.registrations.get(uid);
        if (unsubscribes === undefined)
            return;
        unsubscribes.forEach(unsubscribe => { unsubscribe(); });
        this.registrations.delete(uid);
    }
    isRegistered(uid) {
        return this.registrations.has(uid);
    }
    /// Private helper methods ///
    /**
     * Descendants can attach/resolve their CollisionFacets in a rapid burst (e.g. many pieces of a complex mesh
     * streaming in one after another), each individually triggering a rebuild. Besides the redundant full-subtree
     * walks + geometry merges that causes, CollisionWorld discards a shape update if a newer one for the same uid was
     * requested before the previous one finished building - so a burst arriving faster than the async geometry build
     * can complete means every single attempt gets invalidated by the next one, and the collider never actually lands
     * (the build itself succeeds, it's just always thrown away as stale). Coalescing same-tick triggers into one
     * microtask-deferred rebuild fixes both problems.
     */
    scheduleRebuild(uid) {
        if (this.pendingRebuildUIDs.has(uid))
            return;
        this.pendingRebuildUIDs.add(uid);
        queueMicrotask(() => {
            this.pendingRebuildUIDs.delete(uid);
            this.rebuild(uid);
        });
    }
    rebuild(uid) {
        var _a;
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const facet = model.getFacet(CollisionFacet);
        const ignoreInvisibleChildren = (_a = facet === null || facet === void 0 ? void 0 : facet.ignoreInvisibleChildren) !== null && _a !== void 0 ? _a : false;
        const elements = this.gatherElements(model, model, ignoreInvisibleChildren);
        // Mutate the facet's own shape in place (not via setFacet, to avoid re-triggering facet-changed handling/
        // recursion) so any code reading this model's CollisionFacet directly - not just the collision geometry
        // pipeline - sees the current derived elements instead of the permanently-empty authored placeholder.
        if (facet !== undefined)
            facet.shape = elements;
        this.onRebuild(uid, elements);
    }
    gatherElements(root, current, ignoreInvisibleChildren) {
        const elements = [];
        for (const child of current.getChildren()) {
            // Descendants that are currently hidden (e.g. an inactive training-object state) don't contribute a collider
            // when the compound is marked to ignore them - their own subtree is skipped too since it's hidden along with them.
            if (ignoreInvisibleChildren && !child.isVisible())
                continue;
            const facet = child.getFacet(CollisionFacet);
            if (facet !== undefined && !isCompoundShape(facet.shape)) {
                elements.push({
                    shape: facet.shape,
                    trans: this.computeRelativeTransform(child, root),
                    uid: child.getUID()
                });
            }
            elements.push(...this.gatherElements(root, child, ignoreInvisibleChildren));
        }
        return elements;
    }
    computeRelativeTransform(model, referenceModel) {
        const relativeTransform = worldToLocalTransform(model.getWorldTransform(), referenceModel.getWorldTransform(), new Matrix4());
        const position = new Vector3();
        const orientation = new Quaternion();
        const scale = new Vector3();
        relativeTransform.decompose(position, orientation, scale);
        return {
            pos: position.toArray(),
            ori: orientation.toArray(),
            scale: scale.toArray()
        };
    }
    isDescendantOfOrSelf(model, ancestorUID) {
        let current = model;
        while (current !== undefined) {
            if (current.getUID() === ancestorUID)
                return true;
            current = current.getParent();
        }
        return false;
    }
}
